import type { WorkerProfile } from "@/types/userTypes";
import { AvatarPlaceholder } from "@/components/candidates/AvatarPlaceholder";
import { Avatar, Rating } from "@mui/material";
import { ClockIcon, LocationPinIcon } from "@/components/assets/icons/svgs";
import { getSinceDate } from "@/utils/date";
import { useSelector } from "react-redux";
import { Suspense, lazy, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { encodeId } from "@/utils/idEncoder";
import { InlineLazyFallback } from "@/shared/RouteLoadingFallback";
import CustomButton from "@/shared/button/customButton";
import Popup from "@/shared/popups/popupDetails";
import { toast } from "@/utils/appToast";
import { isAuthenticated } from "@/shared/services/credentialsService";
import { workerPath } from "@/shared/publicPaths";
import {
  getProviderUserId,
  getWorkerProfileId,
} from "@/utils/workerProfileUtils";
import {
  getRequestQuoteLoginUrl,
  seedRequestQuoteDraftFromHomeSearch,
} from "@/utils/requestQuoteDraft";

const RequestQuote = lazy(() => import("@/pages/hire/request-quote"));

type Props = {
  candidate: WorkerProfile;
  selectionMode?: "auto" | "manual" | "none";
  isSelected?: boolean;
  selectionDisabled?: boolean;
  onToggleSelected?: (candidate: WorkerProfile) => void;
  /** When set, Request Quote uses this instead of the popup (e.g. compose page). */
  onRequestQuoteClick?: (candidate: WorkerProfile) => void;
};

const slugifyName = (first?: string, last?: string) => {
  const fullName = `${first || ""} ${last || ""}`.trim();
  return fullName
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");
};

export function WorkerProfileCard({
  candidate,
  selectionMode = "auto",
  isSelected = false,
  selectionDisabled = false,
  onToggleSelected,
  onRequestQuoteClick,
}: Props) {
  const { user } = useSelector((state: any) => state.user || {});
  const { t } = useTranslation();

  const [proposalPopupShow, setProposalPopupShow] = useState(false);

  const providerUserId = getProviderUserId(candidate);
  const workerProfileId = getWorkerProfileId(candidate);

  const profilePath = useMemo(() => {
    const shortId = encodeId(candidate?.id || candidate?._id);
    const slug = `${slugifyName(candidate?.user?.first_name, candidate?.user?.last_name)}-${shortId}`;
    return workerPath(slug);
  }, [candidate?.id, candidate?._id, candidate?.user?.first_name, candidate?.user?.last_name]);

  const isWorkerViewer = user?.type === "worker";

  const handleProposalPopup = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isAuthenticated()) {
      const providerUserIdForDraft = getProviderUserId(candidate);
      seedRequestQuoteDraftFromHomeSearch({
        selectedProvider: providerUserIdForDraft
          ? {
              userId: providerUserIdForDraft,
              profileId: getWorkerProfileId(candidate) || "",
              name: `${candidate?.user?.first_name || ""} ${candidate?.user?.last_name || ""}`.trim(),
              avatar:
                typeof candidate?.user === "object"
                  ? candidate?.user?.avatar
                  : undefined,
            }
          : null,
      });
      return window.location.assign(getRequestQuoteLoginUrl());
    }
    if (!providerUserId) {
      return toast.error(
        t("something_went_wrong") || "Could not resolve provider id."
      );
    }
    if (String(user?._id) === String(providerUserId)) {
      return toast.error(
        t("cannotSendProposalToYourself") ||
          "You cannot send a proposal to yourself."
      );
    }
    if (onRequestQuoteClick) {
      onRequestQuoteClick(candidate);
      return;
    }
    setProposalPopupShow(true);
  };

  const viewProfileClassName = `!rounded-3xl !bg-darkBlue w-full h-8 inline-flex items-center justify-center text-xs text-white ${
    isWorkerViewer ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
  }`;

  return (
    <div className="flex flex-col w-full gap-4 bg-white justify-between p-3 relative rounded-2xl">
      <Popup
        size={"sm"}
        className="bg-darkBlue"
        headerTitle=""
        contentSx={{ padding: 0 }}
        isOpen={proposalPopupShow}
        handleSubmit={() => {}}
        handleClose={() => setProposalPopupShow(false)}
        slotProps={{ preventOutsideClose: true }}
        bodyChildren={
          proposalPopupShow && providerUserId ? (
            <Suspense fallback={<InlineLazyFallback />}>
              <RequestQuote
                workerid={providerUserId}
                workerProfileId={workerProfileId || undefined}
                providerIds={[providerUserId]}
                onSuccess={() => setProposalPopupShow(false)}
              />
            </Suspense>
          ) : null
        }
      />

      {/* Full-page href → Vike SSR onBeforeRender; home filters restored via sessionStorage on Back */}
      <a
        href={profilePath}
        className="flex flex-col w-full gap-4 text-inherit no-underline cursor-pointer"
      >
        <div className="flex md:flex-row gap-4">
          <div className="h-[90px] w-[90px]">
            {candidate.user?.avatar ? (
              <Avatar
                src={candidate.user.avatar}
                alt="Avatar"
                className="h-full w-full object-cover rounded-lg"
              />
            ) : (
              <AvatarPlaceholder />
            )}
          </div>

          <div className="flex flex-col gap-1">
            <h3 className="text-lg font-semibold">
              {candidate.user?.first_name} {candidate.user?.last_name}
            </h3>

            <p className="flex items-center text-black">
              {candidate?.averageRating ? (
                <>
                  <Rating
                    name="minimum-rating"
                    value={Number(candidate.averageRating)}
                    max={1}
                    precision={1}
                    readOnly
                    sx={{ color: "#FFE234 !important" }}
                  />
                  <span className="font-semibold text-sm">
                    {Number(candidate.averageRating).toFixed(1)} ({t("reviews")})
                  </span>
                </>
              ) : (
                <span className="font-semibold text-sm">
                  {t("noReviewsYet")}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 text-gray-600 text-xs ">
          {candidate.hourlyPay && (
            <>
              <ClockIcon className="w-6 h-6" />
              <span className="text-xs whitespace-nowrap">
                ${candidate.hourlyPay}/{t("hourly")}
              </span>
              <span className="h-6 w-[2px] bg-gray-300"></span>
            </>
          )}
          <LocationPinIcon className="w-6 h-6" />
          <span className="text-xs truncate">
            {candidate.locationName || t("remote")}
          </span>
          <span className="h-6 w-[2px] bg-gray-300"></span>
          <ClockIcon className="w-6 h-6" />
          <span className="text-xs truncate">
            {getSinceDate(candidate.createdAt)}
          </span>
        </div>

        <div className="flex flex-col gap-3">
          <h2 className="font-semibold text-sm">{t("servicesOffered")}:</h2>
          <div className="flex flex-nowrap gap-2 w-full">
            {candidate.servicesOffered?.slice(0, 2).map((service) => (
              <span
                title={service?.name}
                key={service?._id}
                className="rounded-3xl truncate border bg-opacity-50 border-darkBlue border-opacity-40 text-darkBlue bg-blue-100 px-4 py-1 text-xs"
              >
                {service?.name}
              </span>
            ))}
            {candidate?.servicesOffered?.length > 3 && (
              <span className="rounded-3xl border bg-opacity-50 border-darkBlue border-opacity-40 text-darkBlue bg-blue-100 px-4 py-1 text-xs ">
                +{candidate?.servicesOffered?.length - 3}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <h2 className="font-semibold text-sm">{t("description")}:</h2>
          <p className="text-xs font-normal">
            {candidate?.description?.slice(0, 325) || "-"}
          </p>
        </div>
      </a>

      <div className="flex items-center gap-2 w-full">
        {isWorkerViewer ? (
          <span className={viewProfileClassName} aria-disabled="true">
            {t("viewProfile")}
          </span>
        ) : (
          <a href={profilePath} className={viewProfileClassName}>
            {t("viewProfile")}
          </a>
        )}

        {selectionMode === "manual" ? (
          !isWorkerViewer ? (
            <label className="flex items-center gap-2 w-full justify-end cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isSelected}
                disabled={selectionDisabled}
                onChange={() => onToggleSelected?.(candidate)}
              />
              <span className="text-xs font-semibold text-darkBlue">
                {isSelected ? "Selected" : "Select"}
              </span>
            </label>
          ) : null
        ) : selectionMode === "none" ? null : (
          <CustomButton
            disabled={isWorkerViewer}
            handleButtonClick={handleProposalPopup}
            label={t("RequestQuote")}
            className={`!rounded-3xl border bg-opacity-10 border-darkBlue border-opacity-40 bg-blue-100 w-full h-8 ${
              isWorkerViewer ? "opacity-50 cursor-not-allowed" : ""
            }`}
            labelStyle="text-xs text-darkBlue"
          />
        )}
      </div>
    </div>
  );
}
